import { Action, Middleware, MiddlewareAPI, Dispatch } from 'redux';
import get from 'lodash.get';

import { types as auth } from 'services/auth';
import { types as customer } from 'services/customer';
import { types as cityPass } from 'services/cityPass';
import { types as events } from 'services/events';
import { types as kiosk } from 'services/kiosk';
import { types as willcall } from 'services/willcall';
import { types as hardware } from 'services/hardware';
import { types as countries } from 'services/countries';
import { types as ticketing, selectors as ticketingSelectors } from 'services/ticketing';
import { types as cart, selectors as cartSelectors } from 'services/cart';
import { selectors as configSelectors } from 'services/configuration';
import { actions as salesErrorActions, SalesErrorState } from 'services/salesErrors';
import { ERROR, UNKNOWN } from 'constants/hardware';
import i18n from 'services/translation/i18n';

/**
 * Middleware that captures any sales errors happening within the kiosk. If you
 * would like an error to be captured, add it and replace `error` with the
 * title of the error display and `additionalInfo` will be the last sentence
 * in the display
 *
 * If you would like to ignore/suppress and error, add it as a case and simply
 * break the case statement. All other errors will be caught into a generic error.
 */
const ASYNC_ERRORS = ['ASYNC_ERROR', 'ASYNC_FATAL_ERROR'];

const getPurchaseErrorHeader = (orderId: string, support: string, configHeader: any): string => {
  let errorHeader = i18n.t('saleError.purchaseErrorHeader');
  if (configHeader) {
    errorHeader = configHeader.replace('{{orderId}}', `#${orderId}`).replace('{{support}}', support);
  }

  return errorHeader;
};

const getPurchaseErrorMessage = (orderId: string, support: string, configMessage: any): string => {
  let errorMessage: string;
  if (orderId) {
    if (configMessage) {
      errorMessage = configMessage.replace('{{orderId}}', `#${orderId}`).replace('{{support}}', support);
    } else {
      errorMessage = i18n.t('saleError.purchaseErrorMsg', { orderId: `#${orderId}`, support });
    }
  } else {
    errorMessage = i18n.t('saleError.requestRefund', { support });
  }

  return errorMessage;
};

const salesErrorMiddleware: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (action: Action) => {
  const hasError = ASYNC_ERRORS.reduce((acc, err) => acc || action.type.includes(err), false);

  if (hasError) {
    const state = store.getState();

    // Configuration Ticketing SupportName
    const config = configSelectors.selectConfiguration(state);
    const ticketingPath: string = ticketingSelectors.selectTicketingConfigPath(state);
    const support = get(config, `${ticketingPath}.supportName.${i18n.language}`, i18n.t('commonText.support'));
    const messaging = get(config, `messaging.${i18n.language}`);
    const flowTemplate: string = get(config, 'configuration.flowTemplate', '');
    const eventAPIDownHeader = get(messaging, 'eventAPIDown.header');
    const eventAPIDownMessage = get(messaging, 'eventAPIDown.message');
    const purchaseErrorHeader = get(messaging, 'purchaseError.header');
    const purchaseErrorMessage = get(messaging, 'purchaseError.message');

    // For purchase api failures checkout will be null, hence taking orderId from releaseParams
    const selectCart = cartSelectors.selectCart(state);
    const orderIdForSaleError = get(selectCart, 'releaseParams.orderId', '');

    const salesError: SalesErrorState = {
      error: undefined,
      additionalInfo: undefined,
      type: undefined
    };

    switch (action.type) {
      case cart.PURCHASE_CART_ASYNC.FATAL_ERROR:
        salesError.error = getPurchaseErrorHeader(orderIdForSaleError, support, purchaseErrorHeader);
        salesError.additionalInfo = getPurchaseErrorMessage(orderIdForSaleError, support, purchaseErrorMessage);
        salesError.type = 'purchase error';
        break;
      case hardware.PAYMENT_SALE_ASYNC.ERROR:
        // Paymentdevice error check
        const paymentDeviceStatus = get(action, 'payload.code', '');
        if ([ERROR, UNKNOWN].includes(paymentDeviceStatus)) {
          salesError.error = eventAPIDownHeader || i18n.t('saleError.eventAPIHeader');
          salesError.additionalInfo = eventAPIDownMessage || i18n.t('saleError.notifySupport', { support });
          salesError.type = 'payment device';
        }
        break;
      case ticketing.GET_EVENTS_ASYNC.ERROR:
      case ticketing.GET_EVENTDETAILS_ASYNC.ERROR:
      case ticketing.CREATE_SESSION_ASYNC.ERROR:
        if (flowTemplate !== 'fair') {
          salesError.error = eventAPIDownHeader || i18n.t('saleError.eventAPIHeader');
          salesError.additionalInfo = eventAPIDownMessage || i18n.t('saleError.notifySupport', { support });
          salesError.type = 'api';
        }
        break;
      case auth.MAINTENANCE_LOGIN_ASYNC.ERROR:
      case auth.ADMIN_LOGIN_ASYNC.ERROR:
      case auth.ADMIN_PIN_VALIDATION_ASYNC.ERROR:
      case cart.RELEASE_CART_ASYNC.ERROR:
      case cart.RESERVE_TICKETS_ASYNC.ERROR:
      case cart.RESERVE_MEMBERSHIP_ASYNC.ERROR:
      case cart.APPLY_DONATION_ASYNC.ERROR:
      case cart.REMOVE_DONATION_ASYNC.ERROR:
      case cart.PURCHASE_CART_ASYNC.ERROR:
      case cart.APPLY_PROMO_CODE_ASYNC.ERROR:
      case cart.APPLY_GIFT_VOUCHER_ASYNC.ERROR:
      case cart.REMOVE_TICKETS_FROM_CART.ERROR:
      case customer.SAVE_CUSTOMER_ASYNC.ERROR:
      case customer.SAVE_SURVEY_ASYNC.ERROR:
      case customer.GET_CUSTOMER_ASYNC.ERROR:
      case customer.GET_LOCATIONS_ASYNC.ERROR:
      case events.LOG_EVENT_ASYNC.ERROR:
      case events.GET_LATEST_EVENTS_ASYNC.ERROR:
      case events.GET_EVENTS_ASYNC.ERROR:
      case hardware.PING_ONE_PRINTER_ASYNC.ERROR:
      case hardware.PRINT_ONE_JOB_ASYNC.ERROR:
      case hardware.SCANNER_PING_ASYNC.ERROR:
      case hardware.PAYMENT_PING_ASYNC.ERROR:
      case hardware.EVENT_DATA_PING_ASYNC.ERROR:
      case hardware.PAYMENT_CANCEL_ASYNC.ERROR:
      case hardware.PAYMENT_VOID_ASYNC.ERROR:
      case hardware.SCANNER_READ_ASYNC.ERROR:
      case kiosk.CREATE_PRINT_TICKETS_ASYNC.ERROR:
      case kiosk.CREATE_ORDER_TICKETS_ASYNC.ERROR:
      case kiosk.REBOOT_ADMIN_KIOSK_ASYNC.ERROR:
      case kiosk.CHECK_KIOSK_VERSION_ASYNC.ERROR:
      case ticketing.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.ERROR:
      case ticketing.VALIDATE_MEMBERSHIP_ASYNC.ERROR:
      case willcall.GET_WILL_CALL_ORDERS.ERROR:
      case willcall.GET_ORDER_DETAILS.ERROR:
      case cityPass.VALIDATE_TICKET.ERROR:
      case countries.GET_COUNTRIES_ASYNC.ERROR:
      case ticketing.GET_MEMBERSHIP_PRODUCT_ASYNC.ERROR:
      case ticketing.TEXT_TICKETS_ASYNC.ERROR:
      case ticketing.EMAIL_TICKETS_ASYNC.ERROR:
      case ticketing.UPDATE_PRINT_COUNT_ASYNC.ERROR:
        break;
      default:
        salesError.error = i18n.t('saleError.defaultError');
        break;
    }

    if (salesError.error || salesError.additionalInfo) store.dispatch(salesErrorActions.setError(salesError));
  }

  next(action);
};

export default salesErrorMiddleware;
